import { IGCData } from 'global/Reducer/dataSlice';
import {
    IAccountsReceivable as FAM_IAccountsReceivable,
    IBenefit as FAM_IBenefit,
    IFeesAndPremiums as FAM_IFeesAndPremiums,
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    INonStandard as FAM_INonStandard,
    IStopLoss as FAM_IStopLoss
} from 'model/FlexibleAttributeModel';
import { $cat_$accounts_receivable_id, $cat_$fees_and_premiums_id, $cat_$group_configuration_id } from 'model/ConfigCategoryConstants';

export type EntityType = 'benefit' | 'groupConfiguration' | 'accountsReceivable' | 'feesAndPremium' | 'stopLoss';

export interface IEntityContext {
    type: EntityType;
    entity: FAM_IBenefit | FAM_IGroupConfigurationData | FAM_IAccountsReceivable | FAM_IFeesAndPremiums | FAM_IStopLoss;
    entityId?: string;
    categoryConfigId?: string;
}

export const getEntityContext = (props: {
    data: IGCData;
    benefit?: FAM_IBenefit;
    groupConfiguration?: FAM_IGroupConfigurationData;
    accountsReceivable?: FAM_IAccountsReceivable;
    feesAndPremium?: FAM_IFeesAndPremiums;
    stopLoss?: FAM_IStopLoss;
    sectionForNSConfigId?: string;
}): IEntityContext | null => {
    if (props.groupConfiguration) {
        return {
            type: 'groupConfiguration',
            entity: props.groupConfiguration,
            entityId: props.groupConfiguration.id,
            categoryConfigId: $cat_$group_configuration_id // group config category ID
        };
    }
    if (props.benefit) {
        return {
            type: 'benefit',
            entity: props.benefit,
            entityId: props.benefit.id,
            categoryConfigId: props.benefit?.bcbs_config_categoryid ?? ''
        };
    }
    if (props.accountsReceivable) {
        

        return {
            type: 'accountsReceivable',
            entity: props.accountsReceivable,
            entityId: props.accountsReceivable.id,
            categoryConfigId: $cat_$accounts_receivable_id // AR category ID
        };
    }
    if (props.feesAndPremium) {
        return {
            type: 'feesAndPremium',
            entity: props.feesAndPremium,
            entityId: props.feesAndPremium.id,
            categoryConfigId: $cat_$fees_and_premiums_id // Fees and Premiums category ID
        };
    }
    if (props.stopLoss) {
        return {
            type: 'stopLoss',
            entity: props.stopLoss,
            entityId: props.stopLoss.id,
            categoryConfigId: props.data.config.cache?.map_bcbs_config_category_named['Stop Loss']?.bcbs_config_categoryid ?? ''
        };
    }
    return null;
};

export const getEntityCollection = (
    json: FAM_IGroupConfigurationData,
    entityType: EntityType
): (FAM_IBenefit | FAM_IAccountsReceivable | FAM_IFeesAndPremiums | FAM_IStopLoss)[] => {
    switch (entityType) {
        case 'benefit':
            return json.benefits || [];
        case 'accountsReceivable':
            return json.claimsARs || [];
        case 'feesAndPremium':
            return json.feesAndPremiums || [];
        case 'stopLoss':
            return json.stopLoss || [];
        // case 'groupConfiguration':
        //     return [json];
        default:
            return [];
    }
};

export const getEntityById = (
    json: FAM_IGroupConfigurationData,
    entityType: EntityType,
    id: string
): FAM_IBenefit | FAM_IGroupConfigurationData | FAM_IAccountsReceivable | FAM_IFeesAndPremiums | FAM_IStopLoss | undefined => {
    if (entityType === 'groupConfiguration' && json.id === id) {
        return json;
    }
    
    const collection = getEntityCollection(json, entityType);
    return collection.find(entity => entity.id === id);
};

export const createEntityUpdatePayload = (entityType: EntityType, entity: any) => {
    switch (entityType) {
        case 'benefit':
            return {
                benefit: entity,
                groupConfiguration: undefined,
                accountsReceivable: undefined,
                feesAndPremium: undefined,
                stopLoss: undefined
            };
        case 'groupConfiguration':
            return {
                benefit: undefined,
                groupConfiguration: entity,
                accountsReceivable: undefined,
                feesAndPremium: undefined,
                stopLoss: undefined
            };
        case 'accountsReceivable':
            return {
                benefit: undefined,
                groupConfiguration: undefined,
                accountsReceivable: entity,
                feesAndPremium: undefined,
                stopLoss: undefined
            };
        case 'feesAndPremium':
            return {
                benefit: undefined,
                groupConfiguration: undefined,
                accountsReceivable: undefined,
                feesAndPremium: entity,
                stopLoss: undefined
            };
        case 'stopLoss':
            return {
                benefit: undefined,
                groupConfiguration: undefined,
                accountsReceivable: undefined,
                feesAndPremium: undefined,
                stopLoss: entity
            };
        default:
            throw new Error(`Unsupported entity type: ${entityType}`);
    }
};

/**
 * Finds an entity by category ID from the JSON data and returns the entity with its type.
 * Useful when iterating through template relationships where you only have the categoryID.
 */
export interface IEntityLookupResult {
    entityType: EntityType;
    entity: FAM_IBenefit | FAM_IGroupConfigurationData | FAM_IAccountsReceivable | FAM_IFeesAndPremiums | FAM_IStopLoss;
    benefit?: FAM_IBenefit;
    accountsReceivable?: FAM_IAccountsReceivable;
    feesAndPremium?: FAM_IFeesAndPremiums;
    groupConfiguration?: FAM_IGroupConfigurationData;
    stopLoss?: FAM_IStopLoss;
}

export const findEntityByCategoryId = (
    json: FAM_IGroupConfigurationData,
    categoryId: string
): IEntityLookupResult | null => {
    // Check if it's the group configuration
    if (json.id === categoryId) {
        return {
            entityType: 'groupConfiguration',
            entity: json,
            groupConfiguration: json
        };
    }

    // Check benefits
    const benefit = json.benefits?.find(b => b.id === categoryId);
    if (benefit) {
        return {
            entityType: 'benefit',
            entity: benefit,
            benefit: benefit
        };
    }

    // Check accounts receivable
    const ar = json.claimsARs?.find(a => a.id === categoryId);
    if (ar) {
        return {
            entityType: 'accountsReceivable',
            entity: ar,
            accountsReceivable: ar
        };
    }

    // Check fees and premiums
    const fp = json.feesAndPremiums?.find(f => f.id === categoryId);
    if (fp) {
        return {
            entityType: 'feesAndPremium',
            entity: fp,
            feesAndPremium: fp
        };
    }

    // Check stop loss
    const sl = json.stopLoss?.find(s => s.id === categoryId);
    if (sl) {
        return {
            entityType: 'stopLoss',
            entity: sl,
            stopLoss: sl
        };
    }

    return null;
};

/**
 * Gets the display name for an entity based on its type
 */
export const getEntityDisplayName = (result: IEntityLookupResult): string => {
    switch (result.entityType) {
        case 'groupConfiguration':
            return result.groupConfiguration?.name ?? 'Group Configuration';
        case 'benefit':
            return result.benefit?.name ?? '';
        case 'accountsReceivable':
            return result.accountsReceivable?.name ?? '';
        case 'feesAndPremium':
            return result.feesAndPremium?.name ?? '';
        case 'stopLoss':
            return result.stopLoss?.name ?? '';
        default:
            return '';
    }
};

/**
 * Finds a non-standard within an entity by non-standard ID
 */
export const findNonStandardInEntity = (
    result: IEntityLookupResult,
    nonStandardId: string
): FAM_INonStandard | undefined => {
    switch (result.entityType) {
        case 'groupConfiguration':
            return result.groupConfiguration?.nonStandards?.find(ns => ns.id === nonStandardId);
        case 'benefit':
            return result.benefit?.nonStandards?.find(ns => ns.id === nonStandardId);
        case 'accountsReceivable':
            return result.accountsReceivable?.nonStandards?.find(ns => ns.id === nonStandardId);
        case 'feesAndPremium':
            return result.feesAndPremium?.nonStandards?.find(ns => ns.id === nonStandardId);
        case 'stopLoss':
            return result.stopLoss?.nonStandards?.find(ns => ns.id === nonStandardId);
        default:
            return undefined;
    }
};

/**
 * Gets all non-standards from an entity lookup result
 */
export const getNonStandardsFromEntity = (
    result: IEntityLookupResult
): FAM_INonStandard[] => {
    switch (result.entityType) {
        case 'groupConfiguration':
            return result.groupConfiguration?.nonStandards ?? [];
        case 'benefit':
            return result.benefit?.nonStandards ?? [];
        case 'accountsReceivable':
            return result.accountsReceivable?.nonStandards ?? [];
        case 'feesAndPremium':
            return result.feesAndPremium?.nonStandards ?? [];
        case 'stopLoss':
            return result.stopLoss?.nonStandards ?? [];
        default:
            return [];
    }
};